import ProfileNameEditor from '../components/ProfileNameEditor';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useDominantColor } from '../utils/dominantColor';
import { SAMPLE_BOOKS } from '../data/homeBooks';
import { useFavorites } from '../utils/favorites';
import { usePlaylist } from '../utils/playlist';
import { useActiveProfile, getActiveProfileId } from '../utils/profile';
import { Avatar, AvatarPicker } from '../components/Avatar';
import PremiumLink from '../components/PremiumLink';
import BookDetailsModal from '../components/BookDetailsModal';
import { ALL_ACHIEVEMENTS, POINT_ACHIEVEMENTS, GENRE_ACHIEVEMENTS } from '../data/achievements';
import '../styles/profile.css';

const NOTIF_ACH_BY_ID = (() => {
  const m = {};
  for (const a of POINT_ACHIEVEMENTS) m[a.id] = a;
  for (const list of Object.values(GENRE_ACHIEVEMENTS)) for (const a of list) m[a.id] = a;
  m['first-favourite']  = { id: 'first-favourite',  label: 'First Favourite',  icon: '⭐' };
  m['bilingual-reader'] = { id: 'bilingual-reader', label: 'Bilingual Reader', icon: '🌐' };
  m['streak-3']         = { id: 'streak-3',         label: '3-Pass Streak',    icon: '🔥' };
  return m;
})();

function notifTime(iso) {
  if (!iso) return '';
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return '';
  const s = Math.max(1, Math.floor((Date.now() - d) / 1000));
  if (s < 60)    return `${s}s ago`;
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const CROC_COVER = '/uploads/books/crocodile/cover-en.png';

const GLOW_PALETTE = ['green', 'purple', 'orange', 'blue', 'pink', 'teal', 'red', 'gold'];
const pickGlow = (b, i) => b.color || GLOW_PALETTE[i % GLOW_PALETTE.length];

const TABS = [
  { id: 'continue',     label: 'Continue Reading', icon: '📖' },
  { id: 'playlist',     label: 'Playlist',         icon: '🎧' },
  { id: 'favourites',   label: 'Favourite Books',  icon: '❤️' },
  { id: 'quizzes',      label: 'My Quizzes',       icon: '🧠' },
  { id: 'achievements', label: 'My Achievements',  icon: '🏆' },
  { id: 'progress',     label: 'My Progress',      icon: '📊' },
];

/* ──────────── Sample data (replace with backend once ready) ──────────── */
const SAMPLE = {
  continue: [
    { id: 'sample', title: 'The Crocodile Who Closed the Lake', chapter: 'Chapter 4', progress: 62, cover: CROC_COVER, emoji: '🐊', color: 'green' },
    { id: 'c2',     title: 'Mariam & the Moonlit Garden',       chapter: 'Chapter 2', progress: 35, cover: CROC_COVER, emoji: '🌙', color: 'purple' },
    { id: 'c3',     title: 'The Brave Little Camel',            chapter: 'Chapter 7', progress: 88, cover: CROC_COVER, emoji: '🐪', color: 'orange' },
    { id: 'c4',     title: 'Stars Over the Desert',             chapter: 'Chapter 1', progress: 14, cover: CROC_COVER, emoji: '✨', color: 'blue' },
    { id: 'c5',     title: 'Zayd and the Rainbow Kite',         chapter: 'Chapter 3', progress: 50, cover: CROC_COVER, emoji: '🪁', color: 'pink' },
  ],
  playlist: [
    { id: 1, title: 'Bedtime Stories — Night 1', tracks: 5, duration: '42m' },
    { id: 2, title: 'Prophet Tales Audio', tracks: 8, duration: '1h 12m' },
  ],
  favourites: [
    { id: 'sample', title: 'The Crocodile Who Closed the Lake', author: 'Deenlit Stories',  cover: CROC_COVER, emoji: '🐊', color: 'green',  starred: true,  hasPlaylist: true },
    { id: 'f2',     title: "Ali & the Lost Camel",              author: 'Sami Malik',     cover: CROC_COVER, emoji: '🐪', color: 'orange', hasPlaylist: true },
    { id: 'f3',     title: 'The Kindness Garden',               author: 'Amina Rahman',   cover: CROC_COVER, emoji: '🌸', color: 'pink',   starred: true },
    { id: 'f4',     title: 'The Little Mosque',                 author: 'Yusuf Ahmed',    cover: CROC_COVER, emoji: '🕌', color: 'teal',   hasPlaylist: true },
    { id: 'f5',     title: 'Stars of the Night',                author: 'Nura Qadir',     cover: CROC_COVER, emoji: '⭐', color: 'purple' },
    { id: 'f6',     title: 'The Secret Cave',                   author: 'Hamza Omar',     cover: CROC_COVER, emoji: '🗺️', color: 'blue',   starred: true },
  ],
  quizzes: [
    { id: 1, title: 'Stories of the Prophets — Quiz 1', score: 8, total: 10, date: 'Apr 10' },
    { id: 2, title: 'Arabic Vocabulary Basics', score: 15, total: 20, date: 'Apr 8' },
  ],
  achievements: [
    { id: 1, title: 'First Book Read', icon: '📚', unlocked: true },
    { id: 2, title: 'Week Streak', icon: '🔥', unlocked: true },
    { id: 3, title: 'Quiz Master', icon: '🎯', unlocked: false },
    { id: 4, title: 'Night Owl Reader', icon: '🌙', unlocked: false },
  ],
  progress: {
    booksRead: 3,
    minutesListened: 248,
    currentStreak: 7,
    quizzesTaken: 5,
  },
};

function SectionTitle({ children }) {
  return <h2 className="profile-section-title">{children}</h2>;
}

function EmptyState({ label }) {
  return <div className="profile-empty">Nothing here yet — {label}</div>;
}

function ContinueCard({ book, index, onReset }) {
  const dominantRgb = useDominantColor(book.cover);
  const style = dominantRgb ? { '--glow': dominantRgb } : undefined;
  const [resetting, setResetting] = useState(false);

  const handleReset = async () => {
    if (resetting) return;
    if (!window.confirm(`Reset progress for "${book.title}"?`)) return;
    setResetting(true);
    try { await onReset(book.id); } finally { setResetting(false); }
  };

  return (
    <div
      className={`profile-card ${dominantRgb ? '' : `glow-${pickGlow(book, index)}`}`}
      style={style}
    >
      <PremiumLink to={`/read/${book.id}`} className={`profile-card-thumb ${book.cover ? 'has-cover' : 'is-art'}`}>
        {book.cover ? (
          <img src={book.cover} alt={book.title} />
        ) : (
          <>
            <div className="profile-cover-emoji">{book.emoji || '📖'}</div>
            <div className="profile-cover-title">{book.title}</div>
          </>
        )}
      </PremiumLink>
      <div className="profile-card-body">
        <PremiumLink to={`/read/${book.id}`} className="profile-card-title">{book.title}</PremiumLink>
        <div className="profile-card-meta">Page {book.page + 1}{book.total ? ` of ${book.total}` : ''}</div>
        <div className="profile-progress">
          <span style={{ width: `${Math.max(2, Math.min(100, book.progress || 0))}%` }} />
        </div>
        <div className="profile-card-sub">{book.progress || 0}% complete</div>
        <div className="profile-card-actions-row">
          <PremiumLink to={`/read/${book.id}`} className="profile-resume-btn">Resume</PremiumLink>
          <button
            type="button"
            className="profile-reset-btn"
            onClick={handleReset}
            disabled={resetting}
          >
            {resetting ? 'Resetting…' : 'Reset'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ──────────── Tab panels ──────────── */
function ContinueReading({ items, onReset }) {
  if (!items.length) return <EmptyState label="start a book to see it here." />;
  return (
    <div className="profile-card-grid">
      {items.map((b, i) => <ContinueCard key={b.id} book={b} index={i} onReset={onReset} />)}
    </div>
  );
}

function PlaylistRow({
  book, isRtl, lang, index, total,
  onRemove, onMove, isDragging, isDragOver, dragHandlers,
}) {
  const displayTitle = (isRtl && book.titleAr) ? book.titleAr : book.title;
  const displayCover = (isRtl && book.coverAr) ? book.coverAr : book.cover;
  const dominantRgb = useDominantColor(displayCover);
  const style = dominantRgb ? { '--glow': dominantRgb } : undefined;
  const handleRemove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onRemove?.(book.id);
  };
  const moveUp = (e) => { e.preventDefault(); e.stopPropagation(); onMove?.(index, -1); };
  const moveDown = (e) => { e.preventDefault(); e.stopPropagation(); onMove?.(index, +1); };
  const cls = [
    'profile-list-item',
    'profile-list-item--glow',
    'profile-list-item--draggable',
    isDragging ? 'is-dragging' : '',
    isDragOver ? 'is-drag-over' : '',
  ].filter(Boolean).join(' ');
  return (
    <li
      className={cls}
      style={style}
      draggable
      onDragStart={(e) => dragHandlers.onDragStart(e, index)}
      onDragOver={(e) => dragHandlers.onDragOver(e, index)}
      onDragLeave={() => dragHandlers.onDragLeave(index)}
      onDrop={(e) => dragHandlers.onDrop(e, index)}
      onDragEnd={dragHandlers.onDragEnd}
    >
      <span
        className="profile-list-handle"
        aria-hidden="true"
        title={isRtl ? 'اسحب لإعادة الترتيب' : 'Drag to reorder'}
      >
        <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
          <circle cx="9" cy="6" r="1.5" /><circle cx="15" cy="6" r="1.5" />
          <circle cx="9" cy="12" r="1.5" /><circle cx="15" cy="12" r="1.5" />
          <circle cx="9" cy="18" r="1.5" /><circle cx="15" cy="18" r="1.5" />
        </svg>
      </span>
      {displayCover
        ? <img className="profile-list-cover" src={displayCover} alt="" />
        : <div className="profile-list-icon">🎧</div>}
      <div className="profile-list-body">
        <div className="profile-list-title">{displayTitle}</div>
        <div className="profile-list-meta">{book.author || '—'}{book.ageRange ? ` · ${isRtl ? 'الأعمار' : 'Ages'} ${book.ageRange}` : ''}</div>
      </div>
      <div className="profile-list-arrows" role="group" aria-label={isRtl ? 'إعادة الترتيب' : 'Reorder'}>
        <button
          type="button"
          className="profile-list-arrow"
          onClick={moveUp}
          disabled={index === 0}
          aria-label={isRtl ? 'تحريك للأعلى' : 'Move up'}
        >
          <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="6 15 12 9 18 15" />
          </svg>
        </button>
        <button
          type="button"
          className="profile-list-arrow"
          onClick={moveDown}
          disabled={index === total - 1}
          aria-label={isRtl ? 'تحريك للأسفل' : 'Move down'}
        >
          <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </div>
      <PremiumLink to={`/listen/${book.id}?lang=${lang}`} className="profile-list-action profile-list-action-play" aria-label={`${isRtl ? 'استمع إلى' : 'Listen to'} ${displayTitle}`}>
        <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
          <path d="M8 5v14l11-7z" />
        </svg>
        {isRtl ? 'تشغيل' : 'Play'}
      </PremiumLink>
      <button type="button" onClick={handleRemove} className="profile-list-action profile-list-action-remove" aria-label={`${isRtl ? 'إزالة' : 'Remove'} ${displayTitle}`}>
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <line x1="6" y1="6" x2="18" y2="18" />
          <line x1="18" y1="6" x2="6" y2="18" />
        </svg>
      </button>
    </li>
  );
}

function Playlist({ items, onRemove, onReorder }) {
  const [lang, setLang] = useState('en');
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  if (!items.length) return <EmptyState label="tap the headphones on any book to queue it up here." />;
  const isRtl = lang === 'ar';

  const reorderTo = (newOrderIds) => {
    onReorder?.(newOrderIds);
  };

  const moveItem = (from, dir) => {
    const to = from + dir;
    if (to < 0 || to >= items.length) return;
    const ids = items.map((b) => String(b.id));
    [ids[from], ids[to]] = [ids[to], ids[from]];
    reorderTo(ids);
  };

  const dragHandlers = {
    onDragStart: (e, i) => {
      setDragIdx(i);
      e.dataTransfer.effectAllowed = 'move';
      try { e.dataTransfer.setData('text/plain', String(i)); } catch {}
    },
    onDragOver: (e, i) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (i !== overIdx) setOverIdx(i);
    },
    onDragLeave: (i) => {
      if (overIdx === i) setOverIdx(null);
    },
    onDrop: (e, dropIdx) => {
      e.preventDefault();
      const from = dragIdx;
      setDragIdx(null);
      setOverIdx(null);
      if (from == null || from === dropIdx) return;
      const ids = items.map((b) => String(b.id));
      const [moved] = ids.splice(from, 1);
      ids.splice(dropIdx, 0, moved);
      reorderTo(ids);
    },
    onDragEnd: () => { setDragIdx(null); setOverIdx(null); },
  };

  return (
    <div className="profile-playlist-section" dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="profile-playlist-toolbar">
        <div className="profile-playlist-count">
          {items.length} {items.length === 1 ? (isRtl ? 'كتاب' : 'book') : (isRtl ? 'كتب' : 'books')}
        </div>
        <div className="profile-playlist-lang" role="group" aria-label="Language">
          <button
            type="button"
            className={`profile-playlist-lang-btn ${lang === 'en' ? 'is-active' : ''}`}
            onClick={() => setLang('en')}
          >EN</button>
          <button
            type="button"
            className={`profile-playlist-lang-btn ${lang === 'ar' ? 'is-active' : ''}`}
            onClick={() => setLang('ar')}
          >ع</button>
        </div>
      </div>
      <ul className="profile-list">
        {items.map((b, i) => (
          <PlaylistRow
            key={b.id}
            book={b}
            isRtl={isRtl}
            lang={lang}
            index={i}
            total={items.length}
            onRemove={onRemove}
            onMove={moveItem}
            isDragging={dragIdx === i}
            isDragOver={overIdx === i && dragIdx !== null && dragIdx !== i}
            dragHandlers={dragHandlers}
          />
        ))}
      </ul>
    </div>
  );
}

function FavouriteCard({ book, index, onToggle, onTogglePlaylist, inPlaylist }) {
  const dominantRgb = useDominantColor(book.cover);
  const style = dominantRgb ? { '--glow': dominantRgb } : undefined;
  const readPath = `/read/${book.id}`;
  const quizPath = `/quiz/${book.id}`;
  const [detailsOpen, setDetailsOpen] = useState(false);
  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onToggle?.(book.id);
  };
  const handlePlaylist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onTogglePlaylist?.(book.id);
  };
  const openDetails = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDetailsOpen(true);
  };
  return (
    <div
      className={`profile-card ${dominantRgb ? '' : `glow-${pickGlow(book, index)}`}`}
      style={style}
    >
      <PremiumLink to={readPath} className={`profile-card-thumb ${book.cover ? 'has-cover' : ''}`}>
        {book.cover ? <img src={book.cover} alt={book.title} /> : (book.emoji || '❤️')}
        <button
          type="button"
          className={`profile-playlist-btn is-button ${inPlaylist ? 'is-active' : ''}`}
          aria-label={inPlaylist ? 'Remove from playlist' : 'Add to playlist'}
          aria-pressed={!!inPlaylist}
          title={inPlaylist ? 'In your playlist' : 'Add to playlist'}
          onClick={handlePlaylist}
        >
          {inPlaylist ? (
            <svg viewBox="0 0 24 24" width="18" height="16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3"  y1="12" x2="3"  y2="12" className="wave-bar wave-1" />
              <line x1="7"  y1="9"  x2="7"  y2="15" className="wave-bar wave-2" />
              <line x1="11" y1="6"  x2="11" y2="18" className="wave-bar wave-3" />
              <line x1="15" y1="9"  x2="15" y2="15" className="wave-bar wave-4" />
              <line x1="19" y1="11" x2="19" y2="13" className="wave-bar wave-5" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 14v-1a9 9 0 0 1 18 0v1" />
              <path d="M21 14v3a2 2 0 0 1-2 2h-1v-6h1a2 2 0 0 1 2 1z" fill="currentColor" stroke="none" />
              <path d="M3 14v3a2 2 0 0 0 2 2h1v-6H5a2 2 0 0 0-2 1z" fill="currentColor" stroke="none" />
            </svg>
          )}
        </button>
        <button
          type="button"
          className={`profile-fav-star is-button ${book.starred ? 'is-filled' : 'is-outline'}`}
          aria-label={book.starred ? 'Remove from favourites' : 'Add to favourites'}
          aria-pressed={!!book.starred}
          onClick={handleToggle}
        >
          {book.starred ? '\u2605' : '\u2606'}
        </button>
        <button
          type="button"
          className="profile-card-details-btn"
          aria-label="Show details"
          title="Show details"
          onClick={openDetails}
        >
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9.5" />
            <line x1="12" y1="11" x2="12" y2="16.5" />
            <circle cx="12" cy="7.5" r="0.4" fill="currentColor" />
          </svg>
          <span>Details</span>
        </button>
      </PremiumLink>
      <div className="profile-card-body">
        <div className="profile-card-title">{book.title}</div>
        <div className="profile-card-meta">
          {book.author}
          {book.ageRange && <span className="home-age-badge"> · Ages {book.ageRange}</span>}
        </div>
        <div className="profile-card-actions">
          <PremiumLink to={readPath} className="card-action-btn card-action-read">Read</PremiumLink>
          <PremiumLink to={quizPath} className="card-action-btn card-action-quiz">Quiz</PremiumLink>
        </div>
      </div>
      <BookDetailsModal
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        book={book}
        readPath={readPath}
        quizPath={quizPath}
      />
    </div>
  );
}

function AssignedBookCard({ book, index, onReset }) {
  const dominantRgb = useDominantColor(book.cover);
  const style = dominantRgb ? { '--glow': dominantRgb } : undefined;
  const [resetting, setResetting] = useState(false);

  const handleReset = async () => {
    if (resetting) return;
    if (!window.confirm(`Reset progress for "${book.title}"?`)) return;
    setResetting(true);
    try { await onReset(book.id); } finally { setResetting(false); }
  };

  return (
    <div
      className={`profile-card ${dominantRgb ? '' : `glow-${pickGlow(book, index)}`}`}
      style={style}
    >
      <PremiumLink to={`/read/${book.id}`} className={`profile-card-thumb ${book.cover ? 'has-cover' : ''}`}>
        {book.cover ? <img src={book.cover} alt={book.title} /> : '📚'}
      </PremiumLink>
      <div className="profile-card-body">
        <PremiumLink to={`/read/${book.id}`} className="profile-card-title">{book.title}</PremiumLink>
        {book.started ? (
          <>
            <div className="profile-card-meta">Page {book.page + 1}{book.total ? ` of ${book.total}` : ''}</div>
            <div className="profile-progress">
              <span style={{ width: `${Math.max(2, Math.min(100, book.progress || 0))}%` }} />
            </div>
            <div className="profile-card-sub">{book.progress || 0}% complete</div>
          </>
        ) : (
          <div className="profile-card-sub">Assigned · not started yet</div>
        )}
        <div className="profile-card-actions-row">
          <PremiumLink to={`/read/${book.id}`} className="profile-resume-btn">
            {book.started ? 'Resume' : 'Start'}
          </PremiumLink>
          {book.started && (
            <button
              type="button"
              className="profile-reset-btn"
              onClick={handleReset}
              disabled={resetting}
            >
              {resetting ? 'Resetting…' : 'Reset'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function AssignedBooks({ items, onReset }) {
  if (!items.length) {
    return <EmptyState label="your teacher hasn't assigned any books yet." />;
  }
  return (
    <div className="profile-card-grid">
      {items.map((b, i) => <AssignedBookCard key={b.id} book={b} index={i} onReset={onReset} />)}
    </div>
  );
}

function Favourites({ items, onToggle, onTogglePlaylist, playlistIds }) {
  if (!items.length) return <EmptyState label="tap the star on any book to save it." />;
  return (
    <div className="profile-card-grid">
      {items.map((b, i) => (
        <FavouriteCard
          key={b.id}
          book={b}
          index={i}
          onToggle={onToggle}
          onTogglePlaylist={onTogglePlaylist}
          inPlaylist={playlistIds.has(String(b.id))}
        />
      ))}
    </div>
  );
}

function fmtQuizDate(d) {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function QuizRow({ q }) {
  const dominantRgb = useDominantColor(q.cover);
  const style = dominantRgb ? { '--glow': dominantRgb } : undefined;
  const bestPct = q.bestTotal > 0 ? Math.round((q.bestScore / q.bestTotal) * 100) : 0;
  const lastPct = q.lastTotal > 0 ? Math.round((q.lastScore / q.lastTotal) * 100) : 0;
  const tone = (p) => (p >= 80 ? 'good' : p >= 50 ? 'mid' : 'low');
  return (
    <li className="profile-list-item profile-list-item--glow" style={style}>
      {q.cover
        ? <img className="profile-list-cover" src={q.cover} alt="" />
        : <div className="profile-list-icon">🧠</div>}
      <div className="profile-list-body">
        <div className="profile-list-title">{q.title}</div>
        <div className="profile-list-meta">
          {q.attempts ? `${q.attempts} attempt${q.attempts === 1 ? '' : 's'}` : ''}
          {q.lastAt ? ` · last ${fmtQuizDate(q.lastAt)}` : ''}
        </div>
      </div>
      <div className="profile-quiz-stack">
        <div className="profile-quiz-stack-item">
          <span className="profile-quiz-stack-label">Best</span>
          <div className={`profile-quiz-badge profile-quiz-badge-best ${tone(bestPct)}`}>{q.bestScore}/{q.bestTotal}</div>
        </div>
        <div className="profile-quiz-stack-item">
          <span className="profile-quiz-stack-label">Recent</span>
          <div className={`profile-quiz-badge profile-quiz-badge-recent ${tone(lastPct)}`}>{q.lastScore}/{q.lastTotal}</div>
        </div>
        <PremiumLink to={`/quiz/${q.bookId}`} className="profile-quiz-retake" title="Retake quiz">↻</PremiumLink>
      </div>
    </li>
  );
}

function Quizzes({ items }) {
  if (!items.length) return <EmptyState label="take a quiz to track your scores." />;
  return (
    <ul className="profile-list">
      {items.map((q) => <QuizRow key={q.id} q={q} />)}
    </ul>
  );
}

function Achievements({ rewards }) {
  const unlocked = new Set((rewards?.achievements || []).map((a) => a.id));
  const unlockedAtMap = new Map((rewards?.achievements || []).map((a) => [a.id, a.unlockedAt]));
  const groups = [
    { id: 'points',     label: 'Noor Points',  list: ALL_ACHIEVEMENTS.filter((a) => a.kind === 'points') },
    { id: 'history',    label: 'History',      list: ALL_ACHIEVEMENTS.filter((a) => a.genre === 'history') },
    { id: 'islam',      label: 'Islamic',      list: ALL_ACHIEVEMENTS.filter((a) => a.genre === 'islam') },
    { id: 'nonFiction', label: 'Non-fiction',  list: ALL_ACHIEVEMENTS.filter((a) => a.genre === 'nonFiction') },
    { id: 'fiction',    label: 'Fiction',      list: ALL_ACHIEVEMENTS.filter((a) => a.genre === 'fiction') },
    { id: 'special',    label: 'Special',      list: ALL_ACHIEVEMENTS.filter((a) => a.kind === 'special') },
  ];
  return (
    <div className="profile-achievements">
      {groups.map((g) => (
        <section key={g.id} className="profile-ach-group">
          <h3 className="profile-ach-group-title">{g.label}</h3>
          <div className="profile-badge-grid">
            {g.list.map((a) => {
              const isUnlocked = unlocked.has(a.id);
              const at = unlockedAtMap.get(a.id);
              return (
                <div key={a.id} className={`profile-badge ${isUnlocked ? 'unlocked' : 'locked'}`} title={a.desc || ''}>
                  <div className="profile-badge-icon">{isUnlocked ? a.icon : '🔒'}</div>
                  <div className="profile-badge-title">{a.label}</div>
                  <div className="profile-badge-state">
                    {isUnlocked
                      ? (at ? new Date(at).toLocaleDateString() : 'Unlocked')
                      : (a.points ? `${a.points} NP` : (a.threshold ? `${a.threshold} ${a.threshold === 1 ? 'book' : 'books'}` : 'Locked'))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

function Progress({ rewards }) {
  const stats = rewards?.stats || {};
  const cards = [
    { label: 'Noor Points',     value: (rewards?.noorPoints || 0).toLocaleString(), icon: '✨' },
    { label: 'Books finished',  value: stats.booksFinished || 0,                    icon: '📚' },
    { label: 'Quizzes passed',  value: (stats.firstPasses || 0) + (stats.secondPasses || 0), icon: '🧠' },
    { label: 'Best streak',     value: stats.bestStreak || 0,                       icon: '🔥' },
    { label: 'Current streak',  value: stats.currentStreak || 0,                    icon: '⚡' },
    { label: 'Achievements',    value: (rewards?.achievements || []).length,        icon: '🏆' },
  ];
  return (
    <div className="profile-stat-grid">
      {cards.map((c) => (
        <div key={c.label} className="profile-stat">
          <div className="profile-stat-icon">{c.icon}</div>
          <div className="profile-stat-value">{c.value}</div>
          <div className="profile-stat-label">{c.label}</div>
        </div>
      ))}
    </div>
  );
}

/* ──────────── Page ──────────── */
const TAB_IDS = new Set(['continue', 'playlist', 'favourites', 'quizzes', 'achievements', 'progress']);

export default function Profile() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const initialTab = (() => {
    const fromHash = (location.hash || '').replace(/^#/, '');
    return TAB_IDS.has(fromHash) ? fromHash : 'continue';
  })();
  const [active, setActive] = useState(initialTab);
  const [progress, setProgress] = useState([]);
  const [apiBooks, setApiBooks] = useState([]);
  const [quizScores, setQuizScores] = useState([]);
  const [rewards, setRewards] = useState(null);
  const favorites = useFavorites();
  const playlist = usePlaylist();
  const activeProfile = useActiveProfile();

  // Sync hash → state (back/forward) and state → hash (clicks)
  useEffect(() => {
    const fromHash = (location.hash || '').replace(/^#/, '');
    const wanted = TAB_IDS.has(fromHash) ? fromHash : 'continue';
    if (wanted !== active) setActive(wanted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  const selectTab = (id) => {
    setActive(id);
    if (location.hash !== `#${id}`) {
      navigate({ pathname: '/profile', hash: `#${id}` }, { replace: false });
    }
  };

  useEffect(() => {
    const raw = localStorage.getItem('adeeb-user');
    if (!raw) {
      navigate('/login', { replace: true });
      return;
    }
    setUser(JSON.parse(raw));
  }, [navigate]);

  // Refetch reader-scoped data whenever the active profile changes — the
  // axios interceptor stamps X-Profile-Id from the active profile, so a
  // teacher (or any user) whose profile gets set *after* the first mount
  // would otherwise have these three fetches go without the header and
  // get 400s back from the server, leaving every tab empty.
  useEffect(() => {
    const token = localStorage.getItem('adeeb-token');
    if (!token) return;
    axios.get('/api/books', { timeout: 4000 })
      .then(({ data }) => setApiBooks(Array.isArray(data) ? data : []))
      .catch(() => setApiBooks([]));
    if (!activeProfile?._id) {
      setProgress([]);
      setQuizScores([]);
      setRewards(null);
      return;
    }
    axios.get('/api/progress', { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setProgress(Array.isArray(data) ? data : []))
      .catch(() => setProgress([]));
    axios.get('/api/quizzes', { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setQuizScores(Array.isArray(data) ? data : []))
      .catch(() => setQuizScores([]));
    axios.get('/api/rewards/me', { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setRewards(data))
      .catch(() => setRewards(null));
  }, [activeProfile?._id]);

  // Flatten all known books (API + samples) into an id-keyed map
  const bookIndex = useMemo(() => {
    const map = new Map();
    apiBooks.forEach((b) => map.set(String(b._id), b));
    Object.values(SAMPLE_BOOKS).forEach((list) => list.forEach((b) => map.set(String(b._id), b)));
    return map;
  }, [apiBooks]);

  // Books the teacher assigned to this reader profile. assignedBookIds
  // lives on the Profile doc (cached into useActiveProfile()), so no
  // extra fetch — just filter the loaded catalogue by id. Progress is
  // merged in from the `progress` state so each card can show its
  // Resume position and conditionally render the Reset button.
  const assignedItems = useMemo(() => {
    const ids = Array.isArray(activeProfile?.assignedBookIds) ? activeProfile.assignedBookIds : [];
    if (!ids.length) return [];
    const progressByBook = new Map(progress.map((p) => [String(p.bookId), p]));
    return ids.map((id) => {
      const b = bookIndex.get(String(id));
      const p = progressByBook.get(String(id));
      const total = p?.totalPages || b?.pages?.length || b?.totalPages || null;
      const pct = (p && total) ? Math.min(100, Math.round(((p.page + 1) / total) * 100)) : null;
      return {
        id: String(id),
        title: b?.title || 'Assigned book',
        titleAr: b?.titleAr || '',
        author: b?.author || '',
        cover: b?.coverImage || b?.cover || '',
        coverImageAr: b?.coverImageAr || '',
        page: p?.page ?? null,
        total,
        progress: pct,
        started: !!p,
      };
    }).filter((it) => it.title !== 'Assigned book' || it.cover); // hide pure-stub rows
  }, [activeProfile?.assignedBookIds, bookIndex, progress]);

  const favouriteItems = useMemo(() => {
    const ids = Array.from(favorites.all());
    return ids.map((id) => {
      const b = bookIndex.get(String(id));
      return {
        id,
        title: b?.title || 'Unknown book',
        titleAr: b?.titleAr || '',
        author: b?.author || '',
        ageRange: b?.ageRange || '',
        cover: b?.coverImage || b?.cover || '',
        coverImageAr: b?.coverImageAr || '',
        description: b?.description || '',
        descriptionAr: b?.descriptionAr || '',
        emoji: b?.emoji,
        color: b?.glow,
        starred: true,
      };
    });
  }, [favorites, bookIndex]);

  const onToggleFavourite = async (bookId) => {
    try { await favorites.toggle(bookId); } catch { /* no-op */ }
  };

  const playlistItems = useMemo(() => {
    const ids = Array.from(playlist.all());
    return ids.map((id) => {
      const b = bookIndex.get(String(id));
      return {
        id,
        title: b?.title || 'Unknown book',
        titleAr: b?.titleAr || '',
        author: b?.author || '',
        ageRange: b?.ageRange || '',
        cover: b?.coverImage || b?.cover || '',
        coverAr: b?.coverImageAr || '',
        emoji: b?.emoji,
        color: b?.glow,
      };
    });
  }, [playlist, bookIndex]);

  const onRemoveFromPlaylist = async (bookId) => {
    try { await playlist.toggle(bookId); } catch { /* no-op */ }
  };

  const onReorderPlaylist = async (orderedIds) => {
    try { await playlist.reorder(orderedIds); } catch { /* no-op — optimistic UI already rolled back */ }
  };

  const quizItems = useMemo(() => {
    return quizScores.map((q) => {
      const b = bookIndex.get(String(q.bookId));
      return {
        id: q._id || q.bookId,
        bookId: q.bookId,
        title: b?.title || 'Quiz',
        cover: b?.coverImage || b?.cover || '',
        bestScore: q.bestScore || 0,
        bestTotal: q.bestTotal || 0,
        bestAt:    q.bestAt,
        lastScore: q.lastScore || 0,
        lastTotal: q.lastTotal || 0,
        lastAt:    q.lastAt,
        attempts:  q.attempts || 0,
      };
    });
  }, [quizScores, bookIndex]);

  const [avatarOpen, setAvatarOpen] = useState(false);

  // Notification badge + popup on the big page-level avatar. Mirrors
  // the navbar's badge/popup behaviour, scoped to /profile so the kid
  // sees the number without having to look up at the navbar. Both
  // surfaces stay in sync via the `adeeb-notifications-change` event.
  const [notifItems, setNotifItems] = useState([]);
  const [notifUnread, setNotifUnread] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const refetchNotifs = async () => {
    const pid = activeProfile?._id;
    if (!pid) { setNotifItems([]); setNotifUnread(0); return; }
    try {
      const r = await axios.get('/api/notifications');
      if (getActiveProfileId() !== pid) return;
      setNotifItems(Array.isArray(r?.data?.items) ? r.data.items : []);
      setNotifUnread(Number.isFinite(r?.data?.unread) ? r.data.unread : 0);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    setNotifItems([]); setNotifUnread(0); setNotifOpen(false);
    const pid = activeProfile?._id;
    if (!pid) { setNotifItems([]); setNotifUnread(0); return; }
    let alive = true;
    (async () => {
      try {
        const r = await axios.get('/api/notifications');
        if (!alive) return;
        setNotifItems(Array.isArray(r?.data?.items) ? r.data.items : []);
        setNotifUnread(Number.isFinite(r?.data?.unread) ? r.data.unread : 0);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProfile?._id]);
  // Refetch when the navbar (or any other surface) marks notifications.
  useEffect(() => {
    const h = () => refetchNotifs();
    window.addEventListener('adeeb-notifications-change', h);
    window.addEventListener('adeeb-discover-change', h);
    window.addEventListener('focus', h);
    const timer = setInterval(h, 15000);
    return () => { window.removeEventListener('adeeb-notifications-change', h); window.removeEventListener('adeeb-discover-change', h); window.removeEventListener('focus', h); clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProfile?._id]);
  useEffect(() => {
    if (!notifOpen) return;
    const close = () => setNotifOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [notifOpen]);
  const onMarkRead = async (id) => {
    try {
      await axios.post(`/api/notifications/${id}/read`, null);
      setNotifItems((list) => list.map((n) => n._id === id ? { ...n, read: true } : n));
      setNotifUnread((c) => Math.max(0, c - 1));
      window.dispatchEvent(new Event('adeeb-notifications-change'));
    } catch { /* ignore */ }
  };
  const onMarkAllRead = async () => {
    try {
      await axios.post('/api/notifications/mark-all-read', null);
      setNotifItems((list) => list.map((n) => ({ ...n, read: true })));
      setNotifUnread(0);
      window.dispatchEvent(new Event('adeeb-notifications-change'));
    } catch { /* ignore */ }
  };
  const onNotifItemClick = (n) => {
    if (!n.read) onMarkRead(n._id);
    setNotifOpen(false);
    if (n.type === 'discover-available') { navigate('/discover'); return; }
    if (n.type === 'book-assigned' && n.bookId) navigate(`/read/${n.bookId}`);
    // achievement-unlocked: stay on /profile, user can scroll to the Achievements tab.
  };
  const [savingAvatar, setSavingAvatar] = useState(false);
  const onPickAvatar = async (id) => {
    if (savingAvatar) return;
    setSavingAvatar(true);
    try {
      const token = localStorage.getItem('adeeb-token');
      // If a reader profile is active, update *that* profile's avatar
      // (this is the kid's page). Falls back to the parent user's
      // avatar when no profile is selected (legacy / pre-pick state).
      if (activeProfile?._id) {
        const { data } = await axios.patch(
          `/api/profiles/${activeProfile._id}`,
          { avatar: id },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 }
        );
        // Sync the in-memory active-profile cache so the navbar +
        // header reflect the change instantly without a refetch.
        const { setActiveProfile } = await import('../utils/profile');
        setActiveProfile({ ...activeProfile, ...data });
      } else {
        const { data } = await axios.patch(
          '/api/auth/me/avatar',
          { avatar: id },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 }
        );
        const fresh = { ...(JSON.parse(localStorage.getItem('adeeb-user') || '{}')), ...data.user };
        localStorage.setItem('adeeb-user', JSON.stringify(fresh));
        window.dispatchEvent(new Event('adeeb-auth-change'));
        setUser(fresh);
      }
      setAvatarOpen(false);
    } catch (e) {
      alert(e?.response?.data?.message || 'Could not save avatar.');
    } finally {
      setSavingAvatar(false);
    }
  };

  const continueItems = useMemo(() => {
    return progress.map((p) => {
      const b = bookIndex.get(String(p.bookId));
      // Prefer the flipbook's real length (saved from Reader) over DB page count
      const total = p.totalPages || b?.pages?.length || b?.totalPages || null;
      const pct = total ? Math.min(100, Math.round(((p.page + 1) / total) * 100)) : null;
      return {
        id: p.bookId,
        title: b?.title || 'Unknown book',
        cover: b?.coverImage || b?.cover || '',
        color: b?.glow,
        page: p.page,
        total,
        progress: pct ?? 0,
        updatedAt: p.updatedAt,
      };
    }).sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
  }, [progress, bookIndex]);

  const resetProgress = async (bookId) => {
    const token = localStorage.getItem('adeeb-token');
    if (!token) return;
    try {
      await axios.delete(`/api/progress/${bookId}`, { headers: { Authorization: `Bearer ${token}` } });
      setProgress((prev) => prev.filter((p) => String(p.bookId) !== String(bookId)));
    } catch { /* ignore */ }
  };

  if (!user) return null;

  return (
    <div className="profile-wrapper">
      <Link to="/" className="floating-back-btn">&#8592; Library</Link>

      <header className="profile-header">
        <div className="profile-avatar-wrap" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="profile-avatar-btn"
            onClick={() => setAvatarOpen(true)}
            aria-label="Change avatar"
            title="Change avatar"
          >
            <Avatar
              value={activeProfile?.avatar || user.avatar}
              name={activeProfile?.name || user.name || user.email}
              size={92}
              className="profile-avatar-art"
            />
            <span className="profile-avatar-edit" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
              </svg>
            </span>
          </button>
          <button
            type="button"
            className={`profile-avatar-notif-badge${notifUnread > 0 ? '' : ' is-zero'}`}
            aria-label={notifUnread > 0 ? `${notifUnread} unread notifications` : 'Notifications (no new messages)'}
            aria-haspopup="menu"
            aria-expanded={notifOpen}
            onClick={(e) => { e.stopPropagation(); setNotifOpen((v) => !v); }}
          >
            {notifUnread > 9 ? '9+' : notifUnread}
          </button>
          {notifOpen && (
            <div className="profile-avatar-notif-popup" role="menu" onClick={(e) => e.stopPropagation()}>
              <div className="profile-avatar-notif-head">
                <span className="profile-avatar-notif-title">Notifications</span>
                {notifUnread > 0 && (
                  <button type="button" className="profile-avatar-notif-clear" onClick={onMarkAllRead}>
                    Mark all read
                  </button>
                )}
              </div>
              {notifItems.length === 0 ? (
                <div className="profile-avatar-notif-empty">You're all caught up.</div>
              ) : (
                <div className="profile-avatar-notif-list">
                  {notifUnread === 0 && (
                    <div className="profile-avatar-notif-caughtup">
                      You're all caught up. Older notifications below.
                    </div>
                  )}
                  {notifItems.slice(0, 8).map((n) => {
                    const ach = n.achievementId ? NOTIF_ACH_BY_ID[n.achievementId] : null;
                    const title = n.type === 'achievement-unlocked' && ach
                      ? `Unlocked: ${ach.label}`
                      : n.message || 'You have a new notification.';
                    const icon = n.type === 'achievement-unlocked' ? (ach?.icon || '🏆') : '📘';
                    return (
                      <button
                        key={n._id}
                        type="button"
                        className={`profile-avatar-notif-item${n.read ? '' : ' is-unread'}`}
                        onClick={() => onNotifItemClick(n)}
                      >
                        <span className="profile-avatar-notif-icon" aria-hidden="true">{icon}</span>
                        <span className="profile-avatar-notif-body">
                          <span className="profile-avatar-notif-t">{title}</span>
                          <span className="profile-avatar-notif-time">{notifTime(n.createdAt)}</span>
                        </span>
                        {!n.read && <span className="profile-avatar-notif-dot" aria-hidden="true" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="profile-identity">
          <h1 className="profile-name">{activeProfile?.name || user.name || 'Reader'}</h1>
          <ProfileNameEditor key={activeProfile?._id} profile={activeProfile} />
          <div className="profile-email">
            {activeProfile
              ? <>Profile under <strong>{user.email}</strong></>
              : user.email}
          </div>
          {!activeProfile && user.provider && user.provider !== 'local' && (
            <div className="profile-provider">Signed in with {user.provider}</div>
          )}
        </div>
        <button
          type="button"
          className="profile-noor-pill"
          onClick={() => selectTab('progress')}
          title="View progress"
        >
          <span className="profile-noor-icon" aria-hidden="true">✨</span>
          <span className="profile-noor-stack">
            <span className="profile-noor-value">{(rewards?.noorPoints || 0).toLocaleString()}</span>
            <span className="profile-noor-label">Noor Points</span>
          </span>
          <span className="profile-noor-ach" aria-hidden="true">
            <span className="profile-noor-ach-num">{(rewards?.achievements || []).length}</span>
            <span className="profile-noor-ach-icon">🏆</span>
          </span>
        </button>
      </header>

      <AvatarPicker
        open={avatarOpen}
        currentValue={user.avatar}
        onClose={() => setAvatarOpen(false)}
        onPick={onPickAvatar}
        saving={savingAvatar}
      />

      <nav className="profile-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            data-guide={`${t.id}-tab`}
            aria-selected={active === t.id}
            className={`profile-tab ${active === t.id ? 'is-active' : ''}`}
            onClick={() => selectTab(t.id)}
          >
            <span className="profile-tab-icon">{t.icon}</span>
            <span>{t.label}</span>
          </button>
        ))}
      </nav>

      <section className="profile-panel">
        {active === 'continue' && (
          <>
            {assignedItems.length > 0 && (
              <>
                <SectionTitle>Assigned by Teacher</SectionTitle>
                <AssignedBooks items={assignedItems} onReset={resetProgress} />
              </>
            )}
            <SectionTitle>Continue Reading</SectionTitle>
            <ContinueReading items={continueItems} onReset={resetProgress} />
          </>
        )}
        {active === 'playlist' && (<><SectionTitle>Playlist</SectionTitle><Playlist items={playlistItems} onRemove={onRemoveFromPlaylist} onReorder={onReorderPlaylist} /></>)}
        {active === 'favourites' && (<><SectionTitle>Favourite Books</SectionTitle><Favourites items={favouriteItems} onToggle={onToggleFavourite} onTogglePlaylist={onRemoveFromPlaylist} playlistIds={playlist.all()} /></>)}
        {active === 'quizzes' && (<><SectionTitle>My Quizzes</SectionTitle><Quizzes items={quizItems} /></>)}
        {active === 'achievements' && (<><SectionTitle>My Achievements</SectionTitle><Achievements rewards={rewards} /></>)}
        {active === 'progress' && (<><SectionTitle>My Progress</SectionTitle><Progress rewards={rewards} /></>)}
      </section>
    </div>
  );
}
